import { useEffect, useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { 
  ArrowRight, 
  ChevronRight, 
  ChevronDown,
  MapPin,
  TrendingUp,
  Home,
  Phone,
  ExternalLink,
  Shield
} from 'lucide-react';
import axios from 'axios';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Update meta description dynamically
const updateMetaDescription = (description) => {
  let metaTag = document.querySelector('meta[name="description"]');
  if (!metaTag) {
    metaTag = document.createElement('meta');
    metaTag.name = 'description';
    document.head.appendChild(metaTag);
  }
  metaTag.content = description;
};

// Section component with scroll animation
const Section = ({ children, className = '' }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  return (
    <motion.section
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.7 }}
      className={className}
    >
      {children}
    </motion.section>
  );
};

const CityPage = () => {
  const { citySlug } = useParams();
  const [cityData, setCityData] = useState(null);
  const [loading, setLoading] = useState(true);

  const allCities = [
    { slug: 'fougeres', name: 'Fougères' },
    { slug: 'rennes', name: 'Rennes' },
    { slug: 'vitre', name: 'Vitré' },
    { slug: 'dol-de-bretagne', name: 'Dol-de-Bretagne' },
    { slug: 'pleine-fougeres', name: 'Pleine-Fougères' },
  ];

  const otherCities = allCities.filter(c => c.slug !== citySlug);

  useEffect(() => {
    const fetchCityData = async () => {
      try {
        const response = await axios.get(`${API}/cities/${citySlug}`);
        setCityData(response.data);
        // Update page title and meta description
        document.title = `Conciergerie Airbnb à ${response.data.name} | Confort Hôte`;
        if (response.data.meta_description) {
          updateMetaDescription(response.data.meta_description);
        }
      } catch (error) {
        console.error('Error fetching city data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCityData();
  }, [citySlug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!cityData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-playfair text-[#F5F5F5] mb-4">Ville non trouvée</h1>
          <Link to="/conciergerie" className="text-[#C5A059] hover:underline">
            Retour à la conciergerie
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20" data-testid={`city-page-${citySlug}`}>
      {/* Hero Section */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Breadcrumb */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex items-center gap-2 text-sm text-[#A3A3A3] mb-8"
          >
            <Link to="/" className="hover:text-[#C5A059] transition-colors">Accueil</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/conciergerie" className="hover:text-[#C5A059] transition-colors">Conciergerie</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-[#C5A059]">{cityData.name}</span>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div>
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="flex items-center gap-3 mb-6"
              >
                <MapPin className="w-5 h-5 text-[#C5A059]" />
                <span className="text-xs uppercase tracking-[0.3em] text-[#C5A059]">
                  Ille-et-Vilaine · Bretagne
                </span>
              </motion.div>
              
              <motion.h1 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-4xl sm:text-5xl lg:text-6xl font-playfair font-medium tracking-tight leading-[1.1] text-[#F5F5F5] mb-8"
              >
                Conciergerie Airbnb<br/>
                <span className="text-gold-gradient">à {cityData.name}</span>
              </motion.h1>
              
              <motion.p 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="text-lg md:text-xl text-[#A3A3A3] font-light leading-relaxed mb-8"
              >
                {cityData.description}
              </motion.p>

              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="flex flex-col sm:flex-row gap-4"
              >
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 bg-[#C5A059] text-black px-8 py-4 text-sm uppercase tracking-[0.15em] font-semibold hover:bg-[#D4AF37] transition-colors duration-300"
                  data-testid="cta-audit-city"
                >
                  <span>Audit gratuit à {cityData.name}</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </motion.div>
            </div>

            {/* Market Data */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.6 }}
            >
              <div className="glass-card p-8 md:p-10">
                <p className="text-xs uppercase tracking-[0.3em] text-[#C5A059] mb-8">
                  Données marché · {cityData.name}
                </p>
                <div className="grid grid-cols-1 gap-6">
                  <div className="flex items-center justify-between pb-6 border-b border-[#262626]">
                    <div className="flex items-center gap-4">
                      <TrendingUp className="w-6 h-6 text-[#C5A059]" />
                      <span className="text-[#A3A3A3]">Taux d'occupation moyen</span>
                    </div>
                    <span className="text-2xl font-playfair text-[#C5A059]">{cityData.taux_occupation}</span>
                  </div>
                  <div className="flex items-center justify-between pb-6 border-b border-[#262626]">
                    <div className="flex items-center gap-4">
                      <span className="text-[#C5A059] text-xl">€</span>
                      <span className="text-[#A3A3A3]">Prix moyen / nuitée</span>
                    </div>
                    <span className="text-2xl font-playfair text-[#C5A059]">{cityData.prix_moyen_nuitee}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <Home className="w-6 h-6 text-[#C5A059]" />
                      <span className="text-[#A3A3A3]">Logements actifs</span>
                    </div>
                    <span className="text-2xl font-playfair text-[#C5A059]">{cityData.logements_actifs}</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* City Image Section */}
      {cityData.image_url && (
        <Section className="py-0">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="relative h-[300px] md:h-[400px] overflow-hidden">
              <img 
                src={cityData.image_url} 
                alt={`${cityData.name} - Bretagne`}
                className="w-full h-full object-cover img-desaturate"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
              <div className="absolute bottom-8 left-8">
                <p className="text-xs uppercase tracking-[0.3em] text-[#C5A059] mb-2">
                  Découvrez
                </p>
                <h3 className="text-2xl md:text-3xl font-playfair text-[#F5F5F5]">
                  {cityData.name}, Ille-et-Vilaine
                </h3>
              </div>
            </div>
          </div>
        </Section>
      )}

      {/* Local Expertise Section */}
      <Section className="py-24 md:py-32 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-[#C5A059] mb-6">
                Expertise terrain
              </p>
              <h2 className="text-3xl sm:text-4xl font-playfair font-normal tracking-tight leading-tight text-[#F5F5F5] mb-8">
                Notre connaissance<br/>de {cityData.name}
              </h2>
              <p className="text-lg text-[#A3A3A3] font-light leading-relaxed mb-8">
                L'équipe Confort Hôte connaît {cityData.name} et ses environs sur le bout des doigts. 
                Cette expertise locale nous permet d'optimiser la performance de votre bien et 
                d'offrir une expérience authentique à vos voyageurs.
              </p>
              <div>
                <p className="text-sm uppercase tracking-wider text-[#C5A059] mb-4">Quartiers couverts</p>
                <div className="flex flex-wrap gap-2">
                  {cityData.quartiers.map((quartier, index) => (
                    <span 
                      key={index}
                      className="px-4 py-2 bg-[#121212] border border-[#262626] text-sm text-[#A3A3A3]"
                    >
                      {quartier}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-[#C5A059] mb-6">
                Ce que nous offrons
              </p>
              <div className="space-y-4">
                {[
                  "Pilotage complet de votre location",
                  "Revenue management adapté au marché local",
                  "Équipe de ménage et maintenance sur place",
                  "Connaissance des spécificités réglementaires",
                  "Réseau de partenaires locaux"
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-4 p-4 bg-[#121212] border border-[#262626]">
                    <div className="w-6 h-6 flex items-center justify-center bg-[#C5A059]/10">
                      <ChevronRight className="w-4 h-4 text-[#C5A059]" />
                    </div>
                    <span className="text-[#F5F5F5]">{item}</span>
                  </div>
                ))}
              </div>

              {/* Insurance Notice */}
              <div className="mt-6 bg-[#C5A059]/5 border border-[#C5A059]/20 p-5">
                <div className="flex items-start gap-3">
                  <Shield className="w-5 h-5 text-[#C5A059] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-[#F5F5F5] font-medium mb-1">
                      Assurance obligatoire
                    </p>
                    <p className="text-xs text-[#A3A3A3] leading-relaxed">
                      Pour confier le pilotage de votre bien à Confort Hôte, une assurance spécifique 
                      à l'activité de location courte durée est requise. Nous vous accompagnons.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* FAQ Section */}
      <Section className="py-24 md:py-32">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-[0.3em] text-[#C5A059] mb-6">
              Questions fréquentes
            </p>
            <h2 className="text-3xl sm:text-4xl font-playfair font-normal tracking-tight text-[#F5F5F5]">
              FAQ · {cityData.name}
            </h2>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {cityData.faq.map((item, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`}
                className="border border-[#262626] bg-[#0A0A0A] px-6"
              >
                <AccordionTrigger className="text-left text-[#F5F5F5] hover:text-[#C5A059] py-6 text-lg font-normal">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="text-[#A3A3A3] font-light leading-relaxed pb-6">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </Section>

      {/* Other Cities Section */}
      <Section className="py-24 md:py-32 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-[#C5A059] mb-6">
                Autres zones
              </p>
              <h2 className="text-3xl sm:text-4xl font-playfair font-normal tracking-tight text-[#F5F5F5]">
                Nous intervenons aussi à
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {otherCities.map((city) => (
              <Link
                key={city.slug}
                to={`/conciergerie/${city.slug}`}
                className="group relative bg-[#121212] border border-[#262626] p-6 hover:border-[#C5A059]/30 transition-colors duration-500"
              >
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-playfair text-[#F5F5F5] group-hover:text-[#C5A059] transition-colors duration-300">
                    {city.name}
                  </h3>
                  <ChevronRight className="w-5 h-5 text-[#A3A3A3] group-hover:text-[#C5A059] group-hover:translate-x-1 transition-transform duration-300" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </Section>

      {/* CTA Section */}
      <Section className="py-24 md:py-32">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-playfair font-normal tracking-tight leading-tight text-[#F5F5F5] mb-6">
            Vous avez un bien à {cityData.name} ?
          </h2>
          <p className="text-lg text-[#A3A3A3] font-light leading-relaxed mb-10 max-w-2xl mx-auto">
            Découvrez le potentiel locatif de votre bien avec notre audit gratuit. 
            Sans engagement, notre équipe vous rappelle sous 24h.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-[#C5A059] text-black px-8 py-4 text-sm uppercase tracking-[0.15em] font-semibold hover:bg-[#D4AF37] transition-colors duration-300"
              data-testid="cta-audit-bottom"
            >
              <span>Demander un audit gratuit</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href="tel:0689224402"
              className="inline-flex items-center justify-center gap-2 border border-[#C5A059]/30 text-[#C5A059] px-8 py-4 text-sm uppercase tracking-[0.15em] hover:border-[#C5A059] hover:bg-[#C5A059]/5 transition-colors duration-300"
            >
              <Phone className="w-4 h-4" />
              <span>06 89 22 44 02</span>
            </a>
          </div>
        </div>
      </Section>
    </div>
  );
};

export default CityPage;
